// src/components/CmsUsers/TokenUsageAllModal.jsx
import React, { useMemo, useState } from "react";
import { RefreshCcw } from "lucide-react";

const CmsTokenAllModal = ({ open, onClose, rows = [], pageSize = 10 }) => {
  const [selectedDate, setSelectedDate] = useState(""); // YYYY-MM-DD
  const [page, setPage] = useState(1);

  const onlyDate = (s) => {
    if (!s) return "";
    const m = String(s).match(/(\d{4})[.\-\/](\d{2})[.\-\/](\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : String(s).slice(0, 10);
  };

  // 최신순 정렬 + 날짜필터
  const filtered = useMemo(() => {
    const sorted = [...rows].sort((a, b) => new Date(b.used_at) - new Date(a.used_at));
    return selectedDate ? sorted.filter((r) => onlyDate(r.used_at) === selectedDate) : sorted;
  }, [rows, selectedDate]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(startIdx, startIdx + pageSize);

  // 10개 단위 페이지 블록
  const blockSize = 10;
  const blockIndex = Math.floor((currentPage - 1) / blockSize);
  const startPage = blockIndex * blockSize + 1;
  const endPage = Math.min(startPage + blockSize - 1, totalPages);
  const showPager = total > pageSize;

  const onChangeDate = (e) => {
    setSelectedDate(e.target.value || "");
    setPage(1);
  };
  const onResetDate = () => {
    setSelectedDate("");
    setPage(1);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 w-[900px] max-w-[95vw] h-[630px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl flex flex-col">
        {/* 헤더 */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-lg font-semibold">토큰 사용 내역(전체)</div>
          <button className="rounded-full p-2 hover:bg-neutral-100" onClick={onClose}>✕</button>
        </div>

        {/* 총건수 + 달력 + 리셋 */}
        <div className="mb-3 flex items-center justify-between text-sm text-neutral-600">
          <div>총 <b>{total}</b> 건</div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <span>사용일</span>
              <input
                type="date"
                value={selectedDate}
                onChange={onChangeDate}
                className="h-8 rounded-md border border-neutral-300 px-2"
              />
            </label>
            <button onClick={onResetDate} className="h-8 rounded-md border border-neutral-300 px-3">
              <RefreshCcw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 표 */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-neutral-50 text-center text-neutral-500">
              <tr>
                <th className="px-3 py-2">사용일시</th>
                <th className="px-3 py-2">사용량</th>
                <th className="px-3 py-2">잔여토큰</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length ? (
                pageRows.map((it) => (
                  <tr key={it.key} className="border-b text-center">
                    <td className="px-3 py-2">{onlyDate(it.used_at)}</td>
                    <td className="px-3 py-2">{it.amount_label}</td>
                    <td className="px-3 py-2">{it.remaining}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-3 py-10 text-center text-neutral-500">데이터가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {showPager && (
          <div className="mt-4 flex items-center justify-center gap-2">
            {/* 11페이지 이상일 때만 이전/다음 */}
            {totalPages > blockSize && (
              <button
                className="rounded-md border border-neutral-300 px-3 py-1 disabled:text-neutral-300"
                onClick={() => setPage(Math.max(1, startPage - 1))}
                disabled={startPage === 1}
              >
                ‹ 이전
              </button>
            )}
            {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((p) => {
              const active = p === currentPage;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`min-w-8 rounded-md border px-3 py-1 ${active ? "border-violet-500 bg-violet-500 text-white" : "border-neutral-300"}`}
                >
                  {p}
                </button>
              );
            })}
            {totalPages > blockSize && (
              <button
                className="rounded-md border border-neutral-300 px-3 py-1 disabled:text-neutral-300"
                onClick={() => setPage(Math.min(totalPages, endPage + 1))}
                disabled={endPage === totalPages}
              >
                다음 ›
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
export default CmsTokenAllModal;